import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useGet, api } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { relDate } from "@/lib/format";

const TYPE = { tech_signal: ["Scalp", "#F59E0B"], pre_news: ["Pre-news", "#60A5FA"], post_news: ["Post-news", "#A78BFA"], dxy_cross: ["Gold/DXY cross", "#FB923C"], dxy_regime: ["Gold/DXY link", "#38BDF8"], price_alert: ["Price alert", "#F472B6"], scorecard: ["Scorecard", "#34D399"], weekly: ["Weekly review", "#F472B6"], reminder: ["Reminder", "#9CA3AF"] };

export function NotificationsBell({ refreshKey }) {
  const { data, reload } = useGet("/notifications", refreshKey);
  const [open, setOpen] = useState(false);
  const unread = data?.unread || 0;
  const notes = data?.notifications || [];

  const markAll = async () => {
    await api.post("/notifications/read-all");
    reload();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    reload();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative w-9 h-9 rounded-lg grid place-items-center bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
        >
          <Bell size={15} className="text-[#9CA3AF]" />
          {unread > 0 && (
            <span data-testid="notifications-unread-badge"
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-mono-num grid place-items-center">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#111827] border-white/10 text-[#F9FAFB] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-display font-semibold text-sm">Alerts</span>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAll}
              className="flex items-center gap-1 text-[11px] text-[#F59E0B] hover:underline">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notes.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-[#6B7280]">No alerts yet.</div>
          ) : (
            notes.map((n) => (
              <button key={n.id} onClick={() => markOne(n.id)}
                data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${n.read ? "opacity-55" : ""}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0" />}
                  <div className="min-w-0">
                    {TYPE[n.type] && <span data-testid={`notification-type-${n.type}`} className="text-[9px] uppercase tracking-wider font-mono-num" style={{ color: TYPE[n.type][1] }}>{TYPE[n.type][0]}</span>}
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-[11px] text-[#9CA3AF] mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-[#6B7280] font-mono-num mt-1">{relDate(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
